import {
  isAccessEndedBody,
  notifyAccessEnded,
  notifyUnauthorized,
  recoverSession,
  signInAgain,
} from '../auth/session';
import { withAuth } from '../auth/token';

async function announceIfAccessEnded(res: Response): Promise<void> {
  const body = await res.json().catch(() => null);
  if (isAccessEndedBody(body)) notifyAccessEnded();
}

/**
 * Thrown when a request did not reach its response headers within the deadline.
 *
 * Its only job is to not be an `AbortError`, and that job is the whole reason the
 * class exists. The hooks treat an AbortError as the user having pressed stop and
 * return silently, so a timeout wearing that name would be swallowed and leave the
 * same "nothing happened" the deadline exists to remove.
 *
 * Callers deliberately do not give it its own user-facing message: a timeout shows
 * the same generic failure as anything else that went wrong. The distinction is for
 * control flow and for the console, not for the reader.
 */
export class TimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`Request timed out after ${timeoutMs}ms`);
    this.name = 'TimeoutError';
  }
}

/**
 * Thrown when a refused request has started the silent sign-in, so the page is about to
 * navigate to the identity provider and come back.
 *
 * Named `AbortError` on purpose, the reverse of `TimeoutError`: the hooks return quietly
 * on that name, which is right here, because anything they rendered now would vanish with
 * the page a moment later, and an error flashing up on the way out reads as the send
 * having failed. The unsent message survives the trip; see `auth/pendingSend.ts`.
 */
export class SigningInAgainError extends Error {
  constructor() {
    super('Signing in again');
    this.name = 'AbortError';
  }
}

/** Every JSON call. Generous for a healthy backend, far short of forever. */
export const REQUEST_TIMEOUT_MS = 15_000;

/** An image upload posts a multi-megabyte base64 body, and the backend validates,
    normalises and stores it before replying, so it gets its own longer deadline. */
export const UPLOAD_TIMEOUT_MS = 60_000;

/** SSE. Higher than a JSON call because `/chat/start` writes two rows before it
    starts streaming, and a cold container adds to that. */
export const STREAM_CONNECT_TIMEOUT_MS = 30_000;

/**
 * `fetch` with a deadline on **reaching the response headers**, not on the whole
 * exchange. That distinction is the whole design:
 *
 * - For the JSON endpoints it is equivalent to a full deadline, because FastAPI only
 *   sends headers once the handler returns, so a stalled handler never gets that far.
 * - For the two SSE streams it is what makes a deadline safe at all. Headers arrive
 *   before the first token, so the timer is cleared long before an answer finishes
 *   and a slow answer can never be cut off for being slow.
 *
 * That second point rests on a backend property rather than a hope, and it is worth
 * naming because breaking it would turn this deadline into an answer-killer with no
 * error to trace: Starlette's `StreamingResponse` sends `http.response.start` before
 * it touches the event generator, and the chat routes set `X-Accel-Buffering: no` so
 * no proxy holds the headers back either. Anything that starts buffering the stream
 * ahead of the first token invalidates the deadline below.
 *
 * Known gap, stated rather than hidden: a stream that delivers headers and then
 * stalls mid-answer is not covered. Catching that needs an idle-gap deadline, and a
 * safe threshold cannot be guessed, because the legitimate gap before the first token
 * spans planning, retrieval and filtering. It needs the real distribution measured
 * first; until then the stop button is the recourse. See issue #170.
 *
 * A caller's `signal` is forwarded rather than replaced, since `fetch` accepts only
 * one. The forwarding is deliberately never detached: the caller's signal has to keep
 * cancelling the body stream after headers arrive, which is how the stop button ends
 * an answer mid-flight.
 */
export async function fetchWithTimeout(
  url: string,
  init: RequestInit = {},
  timeoutMs = REQUEST_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  const external = init.signal;
  if (external) {
    if (external.aborted) controller.abort();
    else external.addEventListener('abort', () => controller.abort());
  }

  try {
    const send = () =>
      fetch(url, {
        ...init,
        headers: withAuth(init.headers),
        signal: controller.signal,
      });
    let res = await send();
    // Every call in the app comes through here, which makes this the one place that sees
    // a session end. A 401 is usually a token that expired while the tab slept, so it gets
    // one renewal and one retry, inside the same deadline, before it is announced. A
    // second 401 is announced whatever happens, so a token the backend refuses outright
    // cannot turn into a renew-and-retry loop.
    //
    // A renewal that fails or times out gets the one silent sign-in before the sign-in
    // screen: the identity provider's cookie usually outlives our tokens, so the person
    // comes straight back. Only then, and not after a retry the backend refused again,
    // because a token refused fresh would make that a loop.
    if (res.status === 401) {
      if (await recoverSession()) res = await send();
      else if (signInAgain()) throw new SigningInAgainError();
    }
    if (res.status === 401) notifyUnauthorized();
    // A 403 is only that when the body says so: other forbidden answers (an admin-only
    // route, say) are the caller's to handle. Read from a clone so the caller still gets
    // its body, and announced asynchronously since the body has to be awaited.
    if (res.status === 403) void announceIfAccessEnded(res.clone());
    return res;
  } catch (err) {
    // The abort we raised ourselves surfaces as a generic AbortError, so translate it
    // back into the reason it happened before it reaches a caller.
    if (err instanceof SigningInAgainError) throw err;
    if (timedOut) throw new TimeoutError(timeoutMs);
    throw err;
  } finally {
    clearTimeout(timer);
  }
}
